/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `pastEventsAccess` for the Caintra Connect frontend.
 * @module utils/pastEventsAccess
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
/**
 * Eligibility and sorting helpers for showing past (non-cancelled) events
 * to admins and collaborators with Reports and/or Attendance access.
 */

import {
  ADMIN_USER_TYPE,
  DATE_PART_ISO_LENGTH,
  parseEndOfDayFromIsoDate,
  RADIX_DECIMAL,
  toLocalCalendarDay,
} from "./eventDateFilters.js";

/** Invited collaborator type excluded from the Reports menu. */
export const REPORTS_EXCLUDED_USER_TYPE = "13";

/** Cancelled status used by business / B2B / breakfast / virtual events. */
export const EVENT_STATUS_CANCELLED = "CANCELLED";

/**
 * Reads a permission flag from either a flat object or the app's permissions array.
 * @param {object|Array<object>|null|undefined} permissions
 * @param {string} key
 * @returns {boolean}
 */
export const hasPermissionFlag = (permissions, key) => {
  if (!permissions) {
    return false;
  }
  if (Array.isArray(permissions)) {
    const entry = permissions.find((permission) =>
      Object.prototype.hasOwnProperty.call(permission, key),
    );
    return Boolean(entry?.[key]);
  }
  return Boolean(permissions[key]);
};

/**
 * Whether the user may see past/expired events in Personalizar, Asistencia, and Reportes.
 * Rule (OR): admin, OR Reports access, OR Attendance access.
 *
 * @param {object|null|undefined} user
 * @param {object|Array<object>|null|undefined} permissions
 * @returns {boolean}
 */
export const canViewPastEvents = (user, permissions) => {
  if (!user) {
    return false;
  }
  const userType = String(user.tipoDeUsuario ?? "");
  if (userType === ADMIN_USER_TYPE) {
    return true;
  }
  if (!user.esColaborador) {
    return false;
  }
  // Reports menu: esColaborador && tipoDeUsuario !== "13"
  if (userType !== REPORTS_EXCLUDED_USER_TYPE) {
    return true;
  }
  // Attendance menu: esColaborador && viewEventAttendees
  return hasPermissionFlag(permissions, "viewEventAttendees");
};

/**
 * Whether an event is cancelled (boolean flag or CANCELLED estatus).
 * @param {object|null|undefined} event
 * @returns {boolean}
 */
export const isEventCancelled = (event) => {
  if (!event) {
    return false;
  }
  if (event.cancelado === true) {
    return true;
  }
  return event.estatus === EVENT_STATUS_CANCELLED;
};

/**
 * @param {Array<object>} events
 * @returns {Array<object>}
 */
export const filterNonCancelledEvents = (events) =>
  (events || []).filter((event) => !isEventCancelled(event));

/** Minutes in an hour, for `HH:mm` arithmetic. */
const MINUTES_PER_HOUR = 60;

/** Milliseconds in a minute, for `HH:mm` arithmetic. */
const MS_PER_MINUTE = 60000;

/**
 * Parses `HH:mm` into minutes. Used for both start time and duration.
 * @param {string|null|undefined} value
 * @returns {number|null}
 */
const parseHoursMinutes = (value) => {
  if (value == null || value === "") {
    return null;
  }
  const [hours, minutes] = String(value).trim().split(":");
  const parsedHours = parseInt(hours, RADIX_DECIMAL);
  const parsedMinutes = parseInt(minutes ?? "0", RADIX_DECIMAL);
  if (Number.isNaN(parsedHours) || Number.isNaN(parsedMinutes)) {
    return null;
  }
  return parsedHours * MINUTES_PER_HOUR + parsedMinutes;
};

/**
 * End-of-day for a calendar date, tolerating full ISO strings.
 * @param {string} value
 * @returns {Date|null}
 */
const parseEndOfDay = (value) => {
  try {
    return parseEndOfDayFromIsoDate(
      String(value).slice(0, DATE_PART_ISO_LENGTH),
    );
  } catch {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
};

/**
 * Best-effort end/occurrence date for sorting past vs future.
 *
 * Single-day events (`fechaEvento`) end at `hora` + `duracion` so actions stay
 * available while the event is running; range events (`fechaFinEvento`) end at
 * local end-of-day. Dates are always read as local calendar days, never as the
 * UTC midnight that `new Date("YYYY-MM-DD")` would produce.
 *
 * @param {object} event
 * @returns {Date|null}
 */
export const getEventSortDate = (event) => {
  if (!event) {
    return null;
  }
  if (event.fechaFinEvento) {
    return parseEndOfDay(event.fechaFinEvento);
  }

  const singleDay = event.fechaEvento ?? event.fechaInicioEvento;
  if (!singleDay) {
    return null;
  }

  const startOfDay = toLocalCalendarDay(singleDay);
  if (!startOfDay) {
    return parseEndOfDay(singleDay);
  }

  const startMinutes = parseHoursMinutes(event.hora);
  const durationMinutes = parseHoursMinutes(event.duracion);
  if (startMinutes == null || durationMinutes == null) {
    return parseEndOfDay(singleDay);
  }

  return new Date(
    startOfDay.getTime() + (startMinutes + durationMinutes) * MS_PER_MINUTE,
  );
};

/**
 * Whether the event end/occurrence date is already in the past.
 * @param {object|null|undefined} event
 * @param {Date} [reference=new Date()]
 * @returns {boolean}
 */
export const isPastEvent = (event, reference = new Date()) => {
  const sortDate = getEventSortDate(event);
  return sortDate != null && sortDate < reference;
};

/**
 * Mixed list: future/ongoing first (ascending), then past (most recent first).
 * @param {Array<object>} events
 * @param {Date} [reference=new Date()]
 * @returns {Array<object>}
 */
export const sortEventsFuturesFirst = (events, reference = new Date()) => {
  const annotated = (events || []).map((event) => {
    const sortDate = getEventSortDate(event);
    const isPast = sortDate != null && sortDate < reference;
    return { event, sortDate, isPast };
  });

  const futures = annotated
    .filter((item) => !item.isPast)
    .sort((a, b) => {
      if (!a.sortDate && !b.sortDate) {
        return 0;
      }
      if (!a.sortDate) {
        return 1;
      }
      if (!b.sortDate) {
        return -1;
      }
      return a.sortDate - b.sortDate;
    });

  const past = annotated
    .filter((item) => item.isPast)
    .sort((a, b) => {
      if (!a.sortDate && !b.sortDate) {
        return 0;
      }
      if (!a.sortDate) {
        return 1;
      }
      if (!b.sortDate) {
        return -1;
      }
      return b.sortDate - a.sortDate;
    });

  return [...futures, ...past].map((item) => item.event);
};
